import { CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { BACKGROUNDS_BY_ID } from '@shared/rules/data/backgrounds'
import { PACKS, toolOptionsFor } from '@shared/rules/data/equipment'
import { spellsFor } from '@shared/rules/data/spells'
import { buildCharacter, needsToolChoice } from '@shared/rules/build'
import { Badge, Panel, Select } from '@/components/ui'
import { ChoiceCard, Explainer, PickChip, PickCounter, SectionLabel, StepIntro } from './shared'
import type { StepProps } from './StepIdentity'

// ---------- Equipment ----------

export function StepEquipment({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const background = BACKGROUNDS_BY_ID.get(draft.backgroundId)
  if (!classBuild || !background) return null

  const built = buildCharacter(draft)
  const toolPrompts = [background.tool.label, classBuild.toolTraining]
    .filter((label): label is string => Boolean(label) && needsToolChoice(label!))

  return (
    <div className="space-y-6">
      <StepIntro title="What are you carrying?">
        Pick a starting kit. The first option is a ready-made set that works out of the box; taking
        the gold instead lets you buy your own gear with your DM. Your background adds a few items
        either way.
      </StepIntro>

      <div className="grid gap-4 lg:grid-cols-2">
        {classBuild.equipmentOptions.map((option) => (
          <ChoiceCard
            key={option.id}
            selected={draft.equipmentOptionId === option.id}
            onSelect={() => update({ equipmentOptionId: option.id })}
            title={option.label}
            badges={<Badge tone="gold">{option.gold + background.startingGold} GP</Badge>}
          >
            {option.items.length > 0 ? (
              <ul className="space-y-0.5">
                {option.items.map((item) => <li key={item}>{item}</li>)}
                {option.packs?.map((pack) => (
                  <li key={pack} className="text-ink-500">
                    {pack} ({(PACKS[pack] ?? []).length} items)
                  </li>
                ))}
              </ul>
            ) : (
              <p>Start with nothing but coin and buy your own kit.</p>
            )}
          </ChoiceCard>
        ))}
      </div>

      {toolPrompts.length > 0 && (
        <Panel className="p-4">
          <SectionLabel>Choose your tools</SectionLabel>
          <p className="mb-3 text-sm text-ink-400">
            Being proficient with a tool means you add your proficiency bonus when you use it.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {toolPrompts.map((label) => (
              <label key={label} className="block text-sm">
                <span className="mb-1.5 block text-xs uppercase tracking-wider text-ink-400">{label}</span>
                <Select
                  value={draft.toolChoices[label] ?? ''}
                  onChange={(e) => update({ toolChoices: { ...draft.toolChoices, [label]: e.target.value } })}
                >
                  <option value="">Choose…</option>
                  {toolOptionsFor(label).map((tool) => <option key={tool} value={tool}>{tool}</option>)}
                </Select>
              </label>
            ))}
          </div>
        </Panel>
      )}

      {built && draft.equipmentOptionId && (
        <Panel className="p-4">
          <SectionLabel>How this leaves you</SectionLabel>
          <div className="grid gap-4 sm:grid-cols-3">
            <Figure label="Armor Class" value={built.armorClass} hint={built.acSource} />
            <Figure label="Hit points" value={built.hpMax} hint={`d${built.hitDie} + Constitution`} />
            <Figure label="Gold" value={`${built.coins.gp} gp`} hint="left to spend" />
          </div>

          {built.attacks.length > 0 && (
            <div className="mt-4 border-t border-ink-700/60 pt-4">
              <p className="mb-2 text-xs uppercase tracking-wider text-ink-500">Your attacks</p>
              <ul className="space-y-1.5">
                {built.attacks.map((attack) => (
                  <li key={attack.name} className="flex flex-wrap items-baseline gap-2 text-sm">
                    <span className="text-ink-100">{attack.name}</span>
                    <span className="font-mono text-gold-400">{attack.bonus} to hit</span>
                    <span className="font-mono text-ink-400">{attack.damage}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}

      <Explainer term="Armor Class">
        Armor Class, or AC, is how hard you are to hit. When something attacks you it rolls a
        twenty-sided die and adds its bonus; if the total is equal to or higher than your AC, it hits.
      </Explainer>
    </div>
  )
}

function Figure({ label, value, hint }: { label: string; value: string | number; hint: string }) {
  return (
    <div className="rounded-xl border border-ink-700/60 bg-ink-900/40 px-3 py-2.5">
      <p className="font-mono text-2xl tabular-nums text-ink-100">{value}</p>
      <p className="text-xs text-ink-300">{label}</p>
      <p className="text-[11px] text-ink-500">{hint}</p>
    </div>
  )
}

// ---------- Spells ----------

export function StepSpells({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  if (!classBuild?.spellcasting) return null

  const { cantrips: cantripCount, prepared, spellbook, note } = classBuild.spellcasting
  const spellCount = spellbook ?? prepared

  const toggle = (list: string[], id: string, max: number): string[] =>
    list.includes(id) ? list.filter((s) => s !== id) : list.length < max ? [...list, id] : list

  return (
    <div className="space-y-6">
      <StepIntro title="Choose your spells" note={note}>
        Cantrips are small spells you can cast as often as you want. Levelled spells are stronger but
        use a spell slot, and you get those back after a long rest.
      </StepIntro>

      {cantripCount > 0 && (
        <Panel className="p-4">
          <SectionLabel action={<PickCounter chosen={draft.cantrips.length} total={cantripCount} noun="chosen" />}>
            Cantrips
          </SectionLabel>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {spellsFor(classBuild.id, 0).map((spell) => (
              <PickChip
                key={spell.id}
                label={spell.name}
                meta={spell.school}
                help={spell.text}
                selected={draft.cantrips.includes(spell.id)}
                disabled={draft.cantrips.length >= cantripCount}
                onToggle={() => update({ cantrips: toggle(draft.cantrips, spell.id, cantripCount) })}
              />
            ))}
          </div>
        </Panel>
      )}

      <Panel className="p-4">
        <SectionLabel action={<PickCounter chosen={draft.spells.length} total={spellCount} noun="chosen" />}>
          First-level spells
        </SectionLabel>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {spellsFor(classBuild.id, 1).map((spell) => (
            <PickChip
              key={spell.id}
              label={spell.name}
              meta={[spell.concentration && 'Conc.', spell.ritual && 'Ritual'].filter(Boolean).join(' · ') || spell.school}
              help={spell.text}
              selected={draft.spells.includes(spell.id)}
              disabled={draft.spells.length >= spellCount}
              onToggle={() => update({ spells: toggle(draft.spells, spell.id, spellCount) })}
            />
          ))}
        </div>
      </Panel>

      <Explainer term="concentration">
        Some spells stay active only while you concentrate on them. You can concentrate on just one
        spell at a time, and taking damage can break it. It is worth having at least one spell that
        does not need concentration.
      </Explainer>
    </div>
  )
}
